import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { SortDirectionType } from '../../core/models/sort-direction';
import { DataResponse } from '../../core/models/data-response';
import { suppressGlobalErrorNavigation } from '../../core/interceptors/error/suppress-global-error';
import { ConfigService } from '../../core/services/config/config.service';
import { UtilityService } from '../../core/services/utility/utility.service';
import { PatientPortalUserRequest } from '../models/patient-portal-user-request';
import { RequestDetail, RequestDetailPatient } from '../models/request-detail';

export interface PatientPortalUserRequestsRequest {
  createdAfterUtc: string | null;
  // Status / assignee filters mirror PatientPortalProxyUserRequestsRequest so
  // the two queue Search contracts stay aligned. Null / false means "no filter".
  workflowStatusTypeId: number | null;
  assignedToPatientPortalAdminUserId: number | null;
  unassignedOnly: boolean;
  pageNumber: number;
  pageSize: number;
  searchTerm: string | null;
  sortColumn: string;
  sortDirection: SortDirectionType;
}

export interface PatientPortalUserRequestsResult {
  requests: PatientPortalUserRequest[];
  totalRecordCount: number;
}

export interface ConfirmRequestBody {
  patientId: number;
  patientPortalAdminUserId: number;
  version: number;
  isMismatchOverride?: boolean;
  mismatchedFields?: string[];
  /** Staff justification for a mismatch override; attached to the audit log entry. */
  overrideJustification?: string;
}

export interface AssignRequestBody {
  /** Staff member to route the request to; null clears the assignment. */
  assignToPatientPortalAdminUserId: number | null;
  version: number;
}

/**
 * Success response from the request mutation endpoints. Carries the request's
 * new optimistic-concurrency version, which callers must fold back into their
 * request state so later mutations don't send a stale version.
 *
 * The mutation methods return this as nullable because `UtilityService.putRequest`
 * unwraps `DataResponse.result`, which is absent when the API answers with an
 * empty envelope — callers must fall back to the version they sent rather than
 * writing `undefined` into the concurrency chain.
 */
export interface RequestVersionResponse {
  version: number;
}

export interface SetStatusRequestBody {
  patientPortalUserRequestWorkflowStatusTypeId: number;
  version: number;
}

export interface DenyRequestBody {
  version: number;
  patientPortalAdminUserId: number;
  patientPortalUserRequestDenialReasonTypeId: number;
  note: string | null;
}

/**
 * A page of patient search results plus the server's total match count. The
 * total is what lets a screen say "showing 25 of 60" instead of claiming 25 is
 * everything.
 */
export interface PatientSearchResult {
  patients: RequestDetailPatient[];
  totalRecordCount: number;
}

/**
 * Body for `Bolt/Patients/Search`. Mirrors the API's `BoltPatientSearchRequest`
 * exactly — every property here is bound and validated server-side.
 *
 * Two fields the client used to send are deliberately gone. `phoneNumber` was
 * confirmed unsupported: the search body has no phone property and the matcher
 * never filters on one, so it was dropped silently while reading as a working
 * criterion (and counting toward a two-criteria gate the server also enforces).
 * `patientPortalAdminUserId` is resolved from the access token — the acting admin
 * is never taken from the request body, and sending it implied the client picks
 * who a PHI search is audited against.
 */
export interface PatientSearchRequest {
  patientPortalUserRequestId: number;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  lastFiveSocialSecurityNumber: string | null;
  medicalRecordNumber: string | null;
  sortDirection: SortDirectionType;
  sortColumn: string;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class PatientPortalUserRequestsService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;
  private readonly requestsEndpoint = `${this.config.adminDashboardApiUrl}/PatientPortalUserRequests`;

  getRequestById(patientPortalUserRequestId: number): Observable<RequestDetail> {
    return this.utilityService.getRequest<RequestDetail>(
      `${this.requestsEndpoint}/${patientPortalUserRequestId}`,
    );
  }

  getRequests(
    request: PatientPortalUserRequestsRequest,
  ): Observable<PatientPortalUserRequestsResult> {
    const searchEndpoint = this.requestsEndpoint + '/Search';
    return this.utilityService
      .postRequest<
        DataResponse<PatientPortalUserRequest[]>
      >(searchEndpoint, request, false, false, true)
      .pipe(
        map((dataResponse) => ({
          requests: dataResponse.result,
          totalRecordCount: dataResponse.totalRecordCount,
        })),
      );
  }

  // Patient search reads from the Bolt database, so it lives under the Bolt
  // route segment (distinct from the main-database Patients endpoints).
  //
  // Returns `totalRecordCount` alongside the page, not just the page: callers
  // request a fixed pageSize with no paging UI, so without the total they
  // would render a truncated page as the complete match set — and on this
  // screen "no match" leads staff to deny the request.
  searchPatients(request: PatientSearchRequest): Observable<PatientSearchResult> {
    return this.utilityService
      .postRequest<
        DataResponse<RequestDetailPatient[]>
      >(
        `${this.config.adminDashboardApiUrl}/Bolt/Patients/Search`,
        request,
        false,
        false,
        true,
        // A rejected search (a criterion the API validates more strictly than the
        // form does) has to render as an inline message on the search screen. The
        // default global navigation would replace the screen with /error, losing
        // the criteria the staff member had typed.
        suppressGlobalErrorNavigation(),
      )
      .pipe(
        map((dataResponse) => ({
          patients: dataResponse.result,
          totalRecordCount: dataResponse.totalRecordCount,
        })),
      );
  }

  confirmRequest(
    patientPortalUserRequestId: number,
    body: ConfirmRequestBody,
  ): Observable<unknown> {
    return this.utilityService.putRequest<unknown>(
      `${this.requestsEndpoint}/${patientPortalUserRequestId}/Confirm`,
      body,
    );
  }

  assignRequest(
    patientPortalUserRequestId: number,
    body: AssignRequestBody,
  ): Observable<RequestVersionResponse | null> {
    return this.utilityService.putRequest<RequestVersionResponse | null>(
      `${this.requestsEndpoint}/${patientPortalUserRequestId}/Assign`,
      body,
    );
  }

  setStatus(
    patientPortalUserRequestId: number,
    body: SetStatusRequestBody,
  ): Observable<RequestVersionResponse | null> {
    return this.utilityService.putRequest<RequestVersionResponse | null>(
      `${this.requestsEndpoint}/${patientPortalUserRequestId}/SetStatus`,
      body,
    );
  }

  denyRequest(patientPortalUserRequestId: number, body: DenyRequestBody): Observable<unknown> {
    return this.utilityService.putRequest<unknown>(
      `${this.requestsEndpoint}/${patientPortalUserRequestId}/Deny`,
      body,
    );
  }
}
